import { ApiService, ApiError } from './api.service'
import { Constants } from '../constants'
import {Entity, EntityService} from './entity.service'
import { Group, Spot } from './group.service'
import moment from 'moment-timezone'

class LocationDropdown {
    constructor(location) {
        this.id = location.id
        this.value = location.name
    }
}

class OpeningTimes {
    constructor(openingTimes) {
        this.day = openingTimes ? openingTimes.day : undefined
        this.times = openingTimes ? openingTimes.times : undefined
    }
}

class OpeningHours {
    constructor(openingHours) {
        this.defaultWeek = openingHours ? openingHours.defaultWeek : undefined
        this.exceptions = openingHours ? openingHours.exceptions : undefined
    }
}

class Exception {
    constructor(exception) {
        this.date = exception ? exception.date : undefined
        this.times = exception ? exception.times : undefined
    }
}

class Location {
    constructor(location) {
        this.id = location ? location.id : undefined
        this.name = location ? location.name : undefined
        this.description = location ? location.description : undefined
        this.useTruckLicensePlateAsBookingOption = location ? location.useTruckLicensePlateAsBookingOption == 1 : undefined
        this.useTrailerLicensePlateAsBookingOption = location ? location.useTrailerLicensePlateAsBookingOption == 1 : undefined
        this.timezoneName = location? location.timezoneName : undefined
        this.timeSlotStartTime = location ? location.timeSlotStartTime : undefined
        this.timeSlotEndTime = location ? location.timeSlotEndTime : undefined
        this.timeSlotLengthInMinutes = location ? location.timeSlotLengthInMinutes : undefined
        this.openingHours = location ? location.openingHours : undefined
        this.alertOnGateUpdate = location ? location.alertOnGateUpdate : undefined
        this.bookingsPrivateForAgent = location ? location.bookingsPrivateForAgent : undefined
        this.groups = location && location.groups ? location.groups.map(o => new Group(o)) || [] : []

        // Extensive properties
        this.region = location ? location.region : undefined
        this.entities = location && location.entities ? location.entities.map(o => new Entity(o)) : []
        this.spots = location && location.spots ? location.spots.map(o => new Spot(o)) : []
    }

    getEntities() {
        return this.entities.length > 0 ? this.entities.map(o => o.name).join(', ') : '-'
    }

    getGroups() {
        var groups = []
        for (const group of this.groups) {
            groups.push(group.name)
        }

        return groups.length > 0 ? groups.join(', ') : '-'
    }

    getSpots() {
        return this.spots.length > 0 ? this.spots.map(o => o.label).join(', ') : '-'
    }
}

const LocationService = {

    getLocations: async function() {
        try {
            const response = await ApiService.get(Constants.ENDPOINT_LOCATIONS)
            return response.data
        }catch(error) {
            throw new ApiError(error)
        }
    },

    getLocation: async function(locationId) {
        try {
            const endpoint = `${Constants.ENDPOINT_LOCATIONS}/${locationId}`
            const response = await ApiService.get(endpoint)
            return response.data
        }catch(error) {
            throw new ApiError(error)
        }
    },

    putLocation: async function(location) {
        try {

            let payload = location
            //payload.openingHours = JSON.parse(location.openingHours)

            const endpoint = `${Constants.ENDPOINT_LOCATIONS}/${location.id}`
            const response = await ApiService.put(endpoint, location)
            return response.data
        } catch (error) {
            throw new ApiError(error)
        }
    },

    consecutiveTimeRanges(timeSlots, timeslotLength) {
        let length = 1
        let list = []
        // If the array is empty,
        // return the list
        if (timeSlots.length == 0) {
            return list
        }

        // Traverse the array from first position
        for (let i = 1; i <= timeSlots.length; i++) {
            // Check the difference between the
            // current and the previous elements
            // If the difference doesn't equal to 1
            // just increment the length variable.
            if (i == timeSlots.length
                || timeSlots[i].index - timeSlots[i - 1].index != 1) {
                // If the range contains
                // only one element.
                // add it into the list.
                if (length == 1) {
                    let range = [timeSlots[0].hours + ":" + timeSlots[0].minutes, timeSlots[0].hours + ":" + timeSlots[0].minutes]
                    list.push(range)
                }
                else {
                    // Build the range between the first
                    // element of the range and the
                    // current previous element as the
                    // last range.
                    let endTime = moment(timeSlots[i - 1].hours + ":" + timeSlots[i - 1].minutes, "HH mm")
                    endTime.add(timeslotLength, 'minutes')
                    let hours = endTime.hour() < 10 ? "0"+ endTime.hour().toString() : endTime.hour().toString()
                    let minutes = endTime.minutes() == 0 ? "0"+endTime.minutes().toString() : endTime.minutes().toString()
                    let endString = hours+":"+minutes
                    let range = [timeSlots[i - length].hours + ":" + timeSlots[i - length].minutes, endString]
                    list.push(range)
                }
                // After finding the first range
                // initialize the length by 1 to
                // build the next range.
                length = 1
            }
            else {
                length++
            }
        }
        return list
    },

    /***
     * convert front end location opening hours to a valid json string that can be recorded in the database
     * this includes translating the individual timeslots (as is used in the front end) to time interval strings
     *
     * @param openingHours
     * @return {{}}
     */
    convertOpeningHours(openingHours) {
        let result = {
            defaultWeek: [],
            exceptions: []
        }

        let exceptions = openingHours.exceptions

        for (let d=0; d < 7; d ++) { // for every day in the week
            let dayTimes = openingHours.defaultWeek[d].times
            let times = { times: [] }
            let range = this.consecutiveTimeRanges(dayTimes, 15)
            for (let r of range) {
                times.times.push({from: r[0], to: r[1]})
            }
            result.defaultWeek.push(times)
        }

        for (let e of exceptions) {
            let times = []
            let range = this.consecutiveTimeRanges(e.times , 15)
            for (let r of range) {
                times.push({from: r[0], to: r[1]})
            }
            result.exceptions.push({
                date: new Date(e.date).toISOString(),
                times: times
            })
        }

        return result
    },

    /**
     * Function that finds booking that are violating with the (new) openingHours
     * @param location
     * @return {Promise<*|*[]>}
     */
    findViolatingBookings: async function(location, openingHours) {
        let entities = await EntityService.getEntities(location.id, moment().toDate(), moment().add(10, 'years')) // get all the bookings for all the entities
        let dayMapping = [6, 0, 1, 2, 3, 4, 5] // translate moment js days to format chooses in the ADT openingHours
        let bookings = []

        // extract all the bookings from the entities
        for (let entity of entities) {
            for (let booking of entity.bookings) {
                // add fields needed in the SortedTable
                booking.entity = entity.name
                booking.date = moment(booking.startDate).toDate().toLocaleDateString()
                const startTime = moment(booking.startDate).format('HH:mm')
                const endTime = moment(booking.endDate).format('HH:mm')
                booking.timeslot = `${startTime} - ${endTime}`
                bookings.push(booking)
            }
        }

        let violations = []
        for (let booking of bookings) {
            let startdate = booking.startDate
            let startMoment = moment(startdate) // translate the booking start date to moment

            let enddate = booking.endDate
            let endMoment = moment(enddate) // translate the booking end date to moment

            let dayIndex = dayMapping[moment(startdate).day()]
            let openingTimes = openingHours.defaultWeek[dayIndex].times // get the closing times
            for (let interval of openingTimes) { // for every interval check
                let startInterval = moment(startdate).set({
                    hour: Number(interval.from.split(':')[0]),
                    minutes: Number(interval.from.split(':')[1]),
                    seconds: 0, milliseconds: 0
                })


                let endInterval = moment(enddate).set({
                    hour: Number(interval.to.split(':')[0]),
                    minutes: Number(interval.to.split(':')[1]),
                    seconds: 0, milliseconds: 0
                })

                if (startInterval.isSameOrBefore(startMoment) && endInterval.isAfter(endMoment)) { // if the booking is between the closing times
                    violations.push(booking)
                }
            }
            // find the bookings during the exception
            let exceptions = openingHours.exceptions // get the exceptions
            for (let exception of exceptions) { // for evert exception
                let exceptionMoment = moment(new Date(exception.date))
                if (startMoment.isSame(exceptionMoment, 'date')) { // if the exception is on the same date of the booking
                    let times = exception.times // get the exception times
                    for (let interval of times) {
                        let startInterval = moment(startdate).set({
                            hour: Number(interval.from.split(':')[0]),
                            minutes: Number(interval.from.split(':')[1]),
                            seconds: 0, milliseconds: 0
                        })


                        let endInterval = moment(enddate).set({
                            hour: Number(interval.to.split(':')[0]),
                            minutes: Number(interval.to.split(':')[1]),
                            seconds: 0, milliseconds: 0
                        })

                        if (startInterval.isSameOrBefore(startMoment) && endInterval.isAfter(endMoment)) { // check if the booking violates these times
                            violations.push(booking)
                        }
                    }
                }
            }
        }

        violations = _.uniq(violations) // only return the unique violations
        return violations
    }

}

export { LocationService, LocationDropdown, Location, OpeningHours, OpeningTimes, Exception }
